/**
 * AuthProvider + useAuth(). Holds the current user + loading state, wires the
 * AuthService, and after a successful sign-in calls the Worker `POST /auth/bootstrap`
 * (idempotent) to ensure the profile + admin claim.
 *
 * The AuthService and bootstrap function are INJECTABLE so tests drive a fake
 * service with no Firebase SDK and no network.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthService, AuthUser } from "./authService.js";
import { bootstrapProfile, type BootstrapResponse } from "./bootstrapClient.js";

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /**
   * True when the server-side bootstrap confirmed the `admin` custom claim for
   * the current user. Authoritative source is the Worker (it sets + verifies the
   * claim); the client only mirrors the bootstrap result. Resets on sign-out.
   */
  isAdmin: boolean;
  /** Fresh Firebase ID token for the current user, or null if signed out. */
  getIdToken(): Promise<string | null>;
  signInEmail(email: string, password: string): Promise<void>;
  signUpEmail(email: string, password: string): Promise<void>;
  signInGoogle(): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Send a password-reset email. Always resolves — callers should show a
   * neutral confirmation to avoid leaking whether the account exists.
   */
  sendPasswordReset(email: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  service: AuthService;
  /** Worker base URL for the bootstrap call. */
  workerUrl: string;
  children: ReactNode;
  /** Injectable bootstrap fn (tests). Defaults to the real HTTP client. */
  bootstrapFn?: (idToken: string) => Promise<BootstrapResponse | null>;
}

export function AuthProvider({
  service,
  workerUrl,
  children,
  bootstrapFn,
}: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Keep the latest bootstrap fn without retriggering the auth subscription.
  const bootstrap = useRef(
    bootstrapFn ?? ((idToken: string) => bootstrapProfile(idToken, { workerUrl })),
  );
  bootstrap.current = bootstrapFn ?? ((idToken: string) => bootstrapProfile(idToken, { workerUrl }));

  // Run the Worker bootstrap once the ID token is available after auth, and
  // mirror the server-confirmed admin claim into state.
  // `forceRefresh=true` on the session-restore path to avoid a Worker 401 on
  // near-expiry cached tokens that would silently strip the admin flag.
  const runBootstrap = useCallback(
    async (forceRefresh = false) => {
      const idToken = await service.getIdToken(forceRefresh);
      if (!idToken) return;
      let result = await bootstrap.current(idToken);
      if (!result) {
        // Transient failure. Retry once after a short delay with a fresh token.
        await new Promise<void>((r) => setTimeout(r, 200));
        const freshToken = await service.getIdToken(true);
        if (!freshToken) return;
        result = await bootstrap.current(freshToken);
      }
      // If the Worker granted the admin claim, force-refresh the ID token so the
      // new token carries the claim — required for /admin/* Worker routes to pass
      // the claim check (403 otherwise).
      if (result?.isAdmin === true) {
        await service.getIdToken(true);
      }
      setIsAdmin(result?.isAdmin === true);
    },
    [service],
  );

  useEffect(() => {
    const unsubscribe = service.onAuthStateChanged((u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        // Trigger bootstrap whenever a user is present — covers both explicit
        // sign-in and the persisted-session restore path (browserLocalPersistence).
        void runBootstrap(true);
      } else {
        setIsAdmin(false); // reset admin flag on sign-out
      }
    });
    return unsubscribe;
  }, [service, runBootstrap]);

  const getIdToken = useCallback(() => service.getIdToken(), [service]);

  const signInEmail = useCallback(
    async (email: string, password: string) => {
      await service.signInEmail(email, password);
      await runBootstrap();
    },
    [service, runBootstrap],
  );

  const signUpEmail = useCallback(
    async (email: string, password: string) => {
      await service.signUpEmail(email, password);
      await runBootstrap();
    },
    [service, runBootstrap],
  );

  const signInGoogle = useCallback(async () => {
    await service.signInGoogle();
    await runBootstrap();
  }, [service, runBootstrap]);

  const signOut = useCallback(async () => {
    await service.signOut();
    setIsAdmin(false);
  }, [service]);

  const sendPasswordReset = useCallback(
    async (email: string) => {
      await service.sendPasswordReset(email);
    },
    [service],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAdmin,
      getIdToken,
      signInEmail,
      signUpEmail,
      signInGoogle,
      signOut,
      sendPasswordReset,
    }),
    [user, loading, isAdmin, getIdToken, signInEmail, signUpEmail, signInGoogle, signOut, sendPasswordReset],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context. Throws if used outside an AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider.");
  return ctx;
}
